import React, { useEffect } from 'react';

import { Controller } from 'react-hook-form';

import { IEmployee } from '@entities/employee/model/types';
import { useGetEmployeeAccesses, useGetTeam } from '@entities/myTeam/lib/hooks';
import { useUpdateEmployeeAccesses } from '@entities/myTeam/lib/hooks/useUpdateEmployeeAccesses';
import { RolePresets } from '@entities/myTeam/model/types';
import { getPresetValue } from '@features/AccessSettingsForm/lib/helpers/getPresetValue';
import { useGetEmployeeFieldValue } from '@features/AccessSettingsForm/lib/hooks/useGetEmployeeFieldValue';
import { keysToSnakeObject } from '@shared/helpers/camelToSnake';
import { useDialog, useHandleForm } from '@shared/hooks';
import { CustomModal, Form, Icon, Select, UiButton, UITooltip } from '@shared/ui';

import cls from './AccessSettingsForm.module.css';
import { AddToAllEstimatesBtn } from './AddToAllEstimatesBtn';
import { AddToAllEstimatesModal } from './AddToAllEstimatesModal';
import { useAccessSettingsForm, useAddToAllEstimates } from '../lib/hooks';
import { IAccessSettingsFormValues } from '../model/types';

interface IAccessSettingsFormProps {
    onCloseCb?: () => void;
    currentClickedId: IEmployee['id'];
}

export const AccessSettingsForm = ({ onCloseCb, currentClickedId }: IAccessSettingsFormProps) => {
    const { t } = useAccessSettingsForm();
    const { isAddToAllEstimatesOpen, onAddToAllEstimatesClick, onCloseAddToAllEstimates } =
        useAddToAllEstimates(onCloseCb);

    const { data } = useGetTeam();
    const currentEmail = useGetEmployeeFieldValue({
        employeesData: data?.employees,
        currentClickedId,
        fieldName: 'email',
    });

    const presetsOptions = [
        { label: t('AccessSettingsForm.presetsOptions.label'), value: 'userSettings' },
        ...data.rolePresets.map((preset) => ({
            label: preset.label,
            value: preset.value,
        })),
    ];

    const { isPending, mutateAsync } = useUpdateEmployeeAccesses(currentClickedId);

    const { isOpen, close: handleClose } = useDialog(true, onCloseCb);
    const { control, handleSubmit, isSubmitting, reset, setValue, watch } =
        useHandleForm<IAccessSettingsFormValues>({
            onSubmit: async (formData) => {
                await mutateAsync({
                    employeeId: currentClickedId,
                    updatedData: {
                        accesses: formData,
                    },
                });
                onCloseCb?.();
                handleClose();
            },
            defaultValues: {},
            resetEnable: false,
        });

    const { isGetEmployeeAccessesLoading, employeeAccessData, isSuccess } =
        useGetEmployeeAccesses(currentClickedId);

    useEffect(() => {
        if (isSuccess) {
            const presetValue = getPresetValue<RolePresets>(
                employeeAccessData?.accesses,
                data.rolePresets
            );

            const defaultValue: Record<string, string> = {
                presets: presetValue,
            };
            employeeAccessData?.accesses.forEach((access) => {
                defaultValue[access.value] = access.selected;
            });

            reset(defaultValue);
        }
    }, [isSuccess, employeeAccessData?.accesses, reset]);

    const onHideModal = () => {
        reset();
        handleClose();
        onCloseCb?.();
    };

    const onSetPresets = (value: string) => {
        setValue('presets', value);
        if (value === 'userSettings') {
            return;
        }

        const combinations = data?.rolePresets.find(
            (preset) => preset.value === value
        )?.combination;
        const formState = watch();

        const formStateKeys = Object.entries(formState)
            .filter(([key, value]) => key !== 'presets' && value)
            .map(([key, value]) => key);

        const combinationSnake = keysToSnakeObject(combinations);

        formStateKeys.forEach((key) => {
            setValue(key, combinationSnake[key]);
        });
    };

    const onSetSelect = (name: string, value: string) => {
        setValue(name, value);
        const formState = watch();

        const formStateArrow = Object.entries(formState)
            .filter(([key]) => key !== 'presets')
            .map(([key, formStateValue]) => ({
                value: key,
                selected: formStateValue,
            }));

        const presetValue = getPresetValue<RolePresets>(formStateArrow, data.rolePresets);

        setValue('presets', presetValue);
    };

    return (
        <CustomModal
            isOpen={isOpen}
            onHideModal={onHideModal}
            contentClasses={cls.Container}
            closeButtonClass={cls.CloseButton}
            isLoading={isGetEmployeeAccessesLoading || isPending}
        >
            <CustomModal.Header classes={cls.CustomModalHeader}>
                {t('AccessSettingsForm.Modal.Header')}
            </CustomModal.Header>
            <CustomModal.Body>
                <Form onSubmit={handleSubmit} id="access-settings-form" className={cls.Form}>
                    <Controller
                        name="presets"
                        control={control}
                        render={({ field }) => {
                            if (field.value) {
                                return (
                                    <Select
                                        isSmartPosition
                                        {...field}
                                        onChange={(value: string) => onSetPresets(value)}
                                        options={presetsOptions}
                                        label={t(
                                            'AccessSettingsForm.return.CustomModal.Body.Controller.Select.label'
                                        )}
                                        needSearch={false}
                                        optionClassName={cls.option}
                                        labelClassName={cls.label}
                                        labelIcon={
                                            <UITooltip
                                                classes={cls.iconWrapper}
                                                tooltipClasses={cls.tooltip}
                                                label={t(
                                                    `AccessSettingsForm.return.CustomModal.Body.Select.labelIcon.UITooltip.label`
                                                )}
                                            >
                                                <span className={cls.question}>
                                                    <Icon iconName="question" fill="currentColor" />
                                                </span>
                                            </UITooltip>
                                        }
                                    />
                                );
                            }
                            return null;
                        }}
                    />
                    {data?.accesses.map((access) => (
                        <Controller
                            key={access.value}
                            name={access.value}
                            control={control}
                            render={({ field }) => {
                                if (field.value) {
                                    return (
                                        <Select
                                            isSmartPosition
                                            {...field}
                                            onChange={(value: string) => {
                                                onSetSelect(access.value, value);
                                            }}
                                            options={access.options}
                                            label={access.label}
                                            id={access.value}
                                            needSearch={false}
                                            optionClassName={cls.option}
                                        />
                                    );
                                }
                                return null;
                            }}
                        />
                    ))}
                </Form>
                <AddToAllEstimatesBtn onClick={onAddToAllEstimatesClick} />
            </CustomModal.Body>
            <CustomModal.Footer classes={cls.CustomModalFooter}>
                <UiButton
                    btnColor="secondary"
                    btnText={t('AccessSettingsForm.Modal.CancelBtn')}
                    onClick={onHideModal}
                />
                <UiButton
                    type="submit"
                    form="access-settings-form"
                    btnColor="primary"
                    btnText={t('AccessSettingsForm.Modal.SubmitBtn')}
                    disabled={isSubmitting}
                />
            </CustomModal.Footer>

            <AddToAllEstimatesModal
                isOpen={isAddToAllEstimatesOpen}
                onClose={onCloseAddToAllEstimates}
                email={currentEmail}
            />
        </CustomModal>
    );
};
